import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Trash2, Link2, Loader, Search } from 'lucide-react';

// Meta interest targeting options
interface MetaInterest {
  id: string;
  name: string;
}

const MUSIC_INTERESTS: MetaInterest[] = [
  { id: '6003341579196', name: 'Pop music' },
  { id: '6003582732907', name: 'Rock music' },
  { id: '6003225556345', name: 'Hip hop music' },
  { id: '6003902397066', name: 'Electronic music' },
  { id: '6003195554098', name: 'Rhythm and blues music' },
  { id: '6003633122583', name: 'Heavy metal music' },
  { id: '6003493980595', name: 'Country music' },
  { id: '6003146442552', name: 'Jazz music' },
  { id: '6003107699532', name: 'Soul music' },
  { id: '6003257757682', name: 'Blues music' },
  { id: '6002951587955', name: 'Classical music' },
  { id: '6003179515414', name: 'Dance music' },
  { id: '6003479860669', name: 'House music' },
  { id: '6003395797326', name: 'Electro (music)' },
  { id: '6003155409305', name: 'Electronic dance music' },
  { id: '853932481331810', name: 'Latin music (genre)' },
  { id: '6003226755338', name: 'World music' },
  { id: '6004139436106', name: 'New wave music' },
  { id: '6003347724581', name: 'Emo (music)' },
  { id: '6003288085327', name: 'Banda (music)' },
  { id: '6003255897001', name: 'Norteño (music)' },
  { id: '6003300315938', name: 'Musica ranchera' },
  { id: '6003248641601', name: 'Regional styles of Mexican music' },
  { id: '6003289088651', name: 'Vocal music' },
];

const MUST_MATCH_INTERESTS: MetaInterest[] = [
  { id: '6002969794329', name: 'Spotify' },
  { id: '937077532996593', name: 'Apple Music' },
  { id: '6003277780179', name: 'YouTube Music' },
  { id: '6003484343998', name: 'Music download' },
  { id: '6002840040679', name: 'Music industry' },
  { id: '6003108826384', name: 'Music festivals' },
  { id: '6003332483177', name: 'Music videos' },
  { id: '6003020834693', name: 'Music' },
];
import { supabase } from '../lib/supabase';

import { toast } from 'react-hot-toast';

interface Release {
  id: string;
  name: string;
  artist_id: string;
  release_date: string;
  status: string;
  ad_budget: number;
  total_budget: number;
  service_package: string;
  is_new_customer: boolean;
  agency_fee: number;
  is_published: boolean;
  spotify_url: string | null;
  created_at: string;
  artists?: {
    name: string;
    facebook_page_id?: string;
  };
}

interface EditReleaseModalProps {
  release: Release;
  onClose: () => void;
  onUpdate: () => void;
}

export const EditReleaseModal: React.FC<EditReleaseModalProps> = ({ release, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: release.name,
    release_date: release.release_date,
    status: release.status,
    ad_budget: release.ad_budget / 100, // Convert from cents to euros
    service_package: release.service_package,
    is_new_customer: release.is_new_customer ?? false,
    agency_fee: release.agency_fee ?? 0,
    is_published: release.is_published,
    spotify_url: release.spotify_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [creatingTikTokCampaign, setCreatingTikTokCampaign] = useState(false);
  const [loadingLink, setLoadingLink] = useState(true);
  const [targeting, setTargeting] = useState<'dach' | 'international'>('dach');
  const [mainInterests, setMainInterests] = useState<MetaInterest[]>([{ id: '6003341579196', name: 'Pop music' }]);
  const [mustMatchInterests, setMustMatchInterests] = useState<MetaInterest[]>([{ id: '6002969794329', name: 'Spotify' }]);
  const [showMainDropdown, setShowMainDropdown] = useState(false);
  const [showMustMatchDropdown, setShowMustMatchDropdown] = useState(false);
  const [mainSearchQuery, setMainSearchQuery] = useState('');
  const [mustMatchSearchQuery, setMustMatchSearchQuery] = useState('');

  const filteredMainInterests = useMemo(() => {
    return MUSIC_INTERESTS.filter(i => 
      !mainInterests.some(m => m.id === i.id) &&
      i.name.toLowerCase().includes(mainSearchQuery.toLowerCase())
    );
  }, [mainInterests, mainSearchQuery]);

  const filteredMustMatchInterests = useMemo(() => {
    return MUST_MATCH_INTERESTS.filter(i => 
      !mustMatchInterests.some(m => m.id === i.id) &&
      i.name.toLowerCase().includes(mustMatchSearchQuery.toLowerCase())
    );
  }, [mustMatchInterests, mustMatchSearchQuery]);

  const addMainInterest = (interest: MetaInterest) => {
    if (mainInterests.length < 4) {
      setMainInterests([...mainInterests, interest]);
    }
    setShowMainDropdown(false);
    setMainSearchQuery('');
  };

  const removeMainInterest = (id: string) => {
    setMainInterests(mainInterests.filter(i => i.id !== id));
  };

  const addMustMatchInterest = (interest: MetaInterest) => {
    if (mustMatchInterests.length < 2) {
      setMustMatchInterests([...mustMatchInterests, interest]);
    }
    setShowMustMatchDropdown(false);
    setMustMatchSearchQuery('');
  };

  const removeMustMatchInterest = (id: string) => {
    setMustMatchInterests(mustMatchInterests.filter(i => i.id !== id));
  };

  // Load on mount
  useEffect(() => {
    setLoadingLink(false);
  }, [release.id]);

  const handleCreateMetaCampaign = async () => {
    // Smartlink URL and Facebook Page ID are now optional
    setCreatingCampaign(true);
    try {
      // Call the edge function in the Reporting project
      const response = await fetch('https://uydhsjvwrgupgfjevqsz.supabase.co/functions/v1/create-meta-ad-campaign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          release_id: release.id,
          artist_name: release.artists?.name || 'Unknown Artist',
          release_name: release.name,
          release_date: release.release_date,
          smartlink_url: null,
          budget_cents: Math.round(formData.ad_budget * 100),
          facebook_page_id: release.artists?.facebook_page_id || null,
          targeting: targeting,
          main_interests: mainInterests.map(i => ({ id: i.id, name: i.name })),
          must_match_interests: mustMatchInterests.map(i => ({ id: i.id, name: i.name })),
        })
      });
      
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.details || data.error || 'Failed to create campaign');
      }

      toast.success('Meta Kampagne erstellt!');
      
      // Open Ads Manager in new tab with search filter for the campaign name
      const artistName = release.artists?.name || 'Unknown Artist';
      const releaseName = release.name;
      const campaignName = `${artistName} - ${releaseName}`;
      const encodedFilter = encodeURIComponent(`SEARCH_BY_CAMPAIGN_GROUP_NAME-STRING\x1ECONTAIN\x1E"${campaignName}"`);
      const adsManagerUrl = `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=626419741407621&business_id=222772888503447&filter_set=${encodedFilter}`;
      window.open(adsManagerUrl, '_blank');
      
      onUpdate();
    } catch (err) {
      console.error('Error creating Meta campaign:', err);
      toast.error(`Fehler: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleCreateTikTokCampaign = async () => {
    setCreatingTikTokCampaign(true);
    try {
      // Call the edge function to create TikTok Smart+ campaign
      const response = await fetch('https://uydhsjvwrgupgfjevqsz.supabase.co/functions/v1/create-tiktok-campaign-v3', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          release_id: release.id,
          artist_name: release.artists?.name || 'Unknown Artist',
          release_name: release.name,
          release_date: release.release_date,
          targeting: targeting, // Use same targeting as Meta
        })
      });
      
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.details || data.error || 'Failed to create TikTok campaign');
      }

      toast.success('TikTok Smart+ Kampagne erstellt!');
      
      // Copy all TikTok auth codes to clipboard
      if (data.auth_codes && data.auth_codes.length > 0) {
        const authCodesText = data.auth_codes.join('\n');
        navigator.clipboard.writeText(authCodesText).then(() => {
          toast.success(`${data.auth_codes.length} TikTok Auth Codes in Zwischenablage kopiert!`);
        }).catch(err => {
          console.error('Failed to copy auth codes to clipboard:', err);
        });
      }
      
      // Open TikTok Ads Manager in new tab
      if (data.tiktok_ads_manager_url) {
        window.open(data.tiktok_ads_manager_url, '_blank');
      }
      
      onUpdate();
    } catch (err) {
      console.error('Error creating TikTok campaign:', err);
      toast.error(`Fehler: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    } finally {
      setCreatingTikTokCampaign(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { error } = await supabase
        .from('releases')
        .update({
          name: formData.name,
          release_date: formData.release_date,
          status: formData.status,
          ad_budget: Math.round(formData.ad_budget * 100), // Convert to cents
          agency_fee: formData.agency_fee,
          total_budget: formData.agency_fee + Math.round(formData.ad_budget * 100),
          service_package: formData.service_package,
          is_new_customer: formData.is_new_customer,
          is_published: formData.is_published,
          spotify_url: formData.spotify_url || null,
        } as any)
        .eq('id' as any, release.id as any);

      if (error) throw error;

      
      toast.success('Release erfolgreich aktualisiert');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating release:', error);
      toast.error('Fehler beim Aktualisieren des Releases');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);

    try {
      const { error } = await supabase
        .from('releases')
        .delete()
        .eq('id' as any, release.id as any);

      if (error) throw error;

      toast.success('Release erfolgreich gelöscht');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting release:', error);
      toast.error('Fehler beim Löschen des Releases');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Release bearbeiten</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Artist Name (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Künstler
            </label>
            <input
              type="text"
              value={release.artists?.name || 'Unbekannt'}
              disabled
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Release Name */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Release Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            />
          </div>

          {/* Release Date */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Release Datum *
            </label>
            <input
              type="date"
              name="release_date"
              value={formData.release_date}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            >
              <option value="submitted">Eingereicht</option>
              <option value="approved">Genehmigt</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="completed">Abgeschlossen</option>
              <option value="cancelled">Abgebrochen</option>
            </select>
          </div>

          {/* Service Package */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Service Paket *
            </label>
            <select
              name="service_package"
              value={formData.service_package}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            >
              <option value="ad_only">Ads only</option>
              <option value="content_only">Content only</option>
              <option value="full">Full</option>
            </select>
          </div>

          {/* Agency Fee */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Agency Fee (€)
            </label>
            <input
              type="number"
              value={formData.agency_fee}
              onChange={(e) => setFormData({ ...formData, agency_fee: parseInt(e.target.value) || 0 })}
              min="0"
              step="1"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Standard: Full 1.500€ | Content 1.000€ | Ads {formData.is_new_customer ? '500' : '400'}€{formData.is_new_customer ? ' (Neukunde)' : ''}
            </p>
          </div>

          {/* Ad Budget */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Media Budget (€) *
            </label>
            <input
              type="number"
              name="ad_budget"
              value={formData.ad_budget}
              onChange={handleChange}
              min="0"
              step="0.01"
              required
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Kann auch unter 650€ sein (Admin-Zugriff)
            </p>
          </div>

          {/* Spotify URL */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Spotify URL
            </label>
            <input
              type="url"
              name="spotify_url"
              value={formData.spotify_url}
              onChange={handleChange}
              placeholder="https://open.spotify.com/track/..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
            />
          </div>

          {/* Is Published */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_published"
              checked={formData.is_published}
              onChange={handleChange}
              className="w-4 h-4 text-[#0000fe] bg-gray-800 border-gray-700 rounded focus:ring-[#0000fe]"
            />
            <label className="ml-2 text-sm text-white">
              Release ist bereits veröffentlicht
            </label>
          </div>

          {/* Campaign Linking Section */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-medium text-white">Campaign Linking</h3>
            </div>

            {loadingLink ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Targeting Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Targeting
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="targeting"
                        value="dach"
                        checked={targeting === 'dach'}
                        onChange={() => setTargeting('dach')}
                        className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 focus:ring-blue-500"
                      />
                      <span className="text-white">DACH</span>
                      <span className="text-xs text-gray-500">(DE, AT, CH)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="targeting"
                        value="international"
                        checked={targeting === 'international'}
                        onChange={() => setTargeting('international')}
                        className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 focus:ring-blue-500"
                      />
                      <span className="text-white">International</span>
                      <span className="text-xs text-gray-500">(30+ countries)</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {targeting === 'dach' 
                      ? 'Deutschland, Österreich, Schweiz • Sprache: Deutsch' 
                      : 'NL, AT, AU, BE, CA, CH, DE, DK, EE, ES, FI, FR, GB, HK, IE, IS, IT, JP, LU, MC, MT, NO, NZ, PT, SE, US • Alle Sprachen'}
                  </p>
                </div>

                {/* Detailed Targeting Section */}
                <div className="space-y-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="text-sm font-medium text-gray-300">Detailed Targeting</div>
                  
                  {/* Main Interests - Include people who match */}
                  <div>
                    <div className="text-xs text-gray-400 mb-2">Include people who match</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {mainInterests.map(interest => (
                        <span key={interest.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600/20 border border-blue-500/30 rounded text-sm text-blue-300">
                          {interest.name}
                          <button
                            type="button"
                            onClick={() => removeMainInterest(interest.id)}
                            className="hover:text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    {mainInterests.length < 4 && (
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-gray-500" />
                          <input
                            type="text"
                            placeholder="Add genre interest..."
                            value={mainSearchQuery}
                            onChange={(e) => {
                              setMainSearchQuery(e.target.value);
                              setShowMainDropdown(true);
                            }}
                            onFocus={() => setShowMainDropdown(true)}
                            onBlur={() => setTimeout(() => setShowMainDropdown(false), 150)}
                            className="flex-1 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                          />
                          <span className="text-xs text-gray-500">{mainInterests.length}/4</span>
                        </div>
                        {showMainDropdown && filteredMainInterests.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg shadow-lg">
                            {filteredMainInterests.map(interest => (
                              <button
                                key={interest.id}
                                type="button"
                                onClick={() => addMainInterest(interest)}
                                className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
                              >
                                {interest.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Must Also Match Interests */}
                  <div>
                    <div className="text-xs text-gray-400 mb-2">and must also match</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {mustMatchInterests.map(interest => (
                        <span key={interest.id} className="inline-flex items-center gap-1 px-2 py-1 bg-green-600/20 border border-green-500/30 rounded text-sm text-green-300">
                          {interest.name}
                          <button
                            type="button"
                            onClick={() => removeMustMatchInterest(interest.id)}
                            className="hover:text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    {mustMatchInterests.length < 2 && (
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-gray-500" />
                          <input
                            type="text"
                            placeholder="Add streaming platform..."
                            value={mustMatchSearchQuery}
                            onChange={(e) => {
                              setMustMatchSearchQuery(e.target.value);
                              setShowMustMatchDropdown(true);
                            }}
                            onFocus={() => setShowMustMatchDropdown(true)}
                            onBlur={() => setTimeout(() => setShowMustMatchDropdown(false), 150)}
                            className="flex-1 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
                          />
                          <span className="text-xs text-gray-500">{mustMatchInterests.length}/2</span>
                        </div>
                        {showMustMatchDropdown && filteredMustMatchInterests.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg shadow-lg">
                            {filteredMustMatchInterests.map(interest => (
                              <button
                                key={interest.id}
                                type="button"
                                onClick={() => addMustMatchInterest(interest)}
                                className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
                              >
                                {interest.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">
                    Targeting: {mainInterests.map(i => i.name).join(' OR ')} {mustMatchInterests.length > 0 && `AND (${mustMatchInterests.map(i => i.name).join(' OR ')})`}
                  </p>
                </div>

                {/* Create Meta Campaign Button */}
                <button
                  type="button"
                  onClick={handleCreateMetaCampaign}
                  disabled={creatingCampaign}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {creatingCampaign ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02Z"/>
                    </svg>
                  )}
                  {creatingCampaign ? 'Erstelle Kampagne...' : 'Meta Kampagne erstellen'}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Erstellt eine pausierte Conversion-Kampagne mit {formData.ad_budget}€ Budget
                </p>

                {/* Create TikTok Campaign Button */}
                <button
                  type="button"
                  onClick={handleCreateTikTokCampaign}
                  disabled={creatingTikTokCampaign}
                  className="w-full px-4 py-2 bg-black hover:bg-gray-900 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2 border border-gray-700"
                >
                  {creatingTikTokCampaign ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  )}
                  {creatingTikTokCampaign ? 'Erstelle Kampagne...' : 'TikTok Smart+ Kampagne erstellen'}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Erstellt eine pausierte Smart+ Kampagne: 3 Tage, 20€/Tag, 60€ Lifetime Budget
                </p>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              style={{ backgroundColor: '#0000fe' }}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>

          {/* Delete Section */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Gefahrenzone</h3>
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full px-4 py-2 bg-red-900/20 hover:bg-red-900/30 text-red-400 border border-red-800 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Release löschen
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-400">
                  Bist du sicher? Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {saving ? 'Löschen...' : 'Endgültig löschen'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
